<template>
  <IntegrationWorkflowWrapper
    v-model:is-enabled="isEnabled"
    title="Push to Account Object"
    description="Push partner overlap data into Salesforce’s standard Account object. This makes it available across the workflows, reports, dashboards, and processes your revenue team already uses every day."
  >
    <!-- TODO: workflow status will live here -->
    <AccountObjectFields v-if="integrationProfileType === 'salesforce'" />
    <div class="flex flex-col gap-16 mt-16">
      <PartnerFieldMapping
        :partner-fields="sharedPartnerFields"
        :crm-fields="integrationCRMSources"
        :mapping="fakeMappingOne"
        :index="0"
        @changed:mapping="updateMapping"
        @deleted:mapping="deleteMapping"
      />
    </div>
  </IntegrationWorkflowWrapper>
</template>

<script setup lang="ts">
import { HttpResponse } from 'msw';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { enableMocks, typedHttp, worker } from '@/mocks';
import { useFeedsStore, usePartnersStore, useSourcesStore } from '@/stores';
import { IntegrationProfileType } from '@/types/integration_profiles';
import { Partner } from '@/types/partners';

import AccountObjectFields from './AccountObjectFields.vue';
import IntegrationWorkflowWrapper from './IntegrationWorkflowWrapper.vue';
import PartnerFieldMapping, { Mapping } from './PartnerFieldMapping.vue';

const { integrationProfileType } = defineProps<{
  integrationProfileType: IntegrationProfileType | undefined;
}>();

const hardeesUUID = '03cc3cbf-8e42-4648-80a4-77b45eb5566d';
const lucasFilmUUID = '03cc3cbe-48f4-40be-beb8-afb7c7319c9b';
const northDakotaStateUniversity = '03cc3cbe-3d89-4b1d-81d9-14dcfb5a6f6b';

const fakeMappingOne = ref<Mapping>({
  id: 'foo',
  source_field_id: 339,
  partner_organization_uuid: northDakotaStateUniversity,
  partner_source_field_id: 21,
});

const buildFakeFields = () => {
  const firstBatch = Array.from({ length: 10 }, (_, i) => ({
    nickname: `Source Field ${i}`,
    column: `source_field_${i}`,
    source_field_id: i,
    organization_uuid: hardeesUUID,
  }));

  const secondBatch = Array.from({ length: 10 }, (_, i) => ({
    nickname: `Source Field ${i + 10}`,
    column: `source_field_${i + 10}`,
    source_field_id: i + 10,
    organization_uuid: lucasFilmUUID,
  }));

  const thirdBatch = Array.from({ length: 10 }, (_, i) => ({
    nickname: `Source Field ${i + 20}`,
    column: `source_field_${i + 20}`,
    source_field_id: i + 20,
    organization_uuid: northDakotaStateUniversity,
  }));
  return [...thirdBatch, ...firstBatch, ...secondBatch];
};

type SharedPartnerField = {
  nickname: string;
  column: string;
  source_field_id: number;
  organization_uuid: string;
};

export interface PartnerOption extends Partner {
  label: string;
  value: number;
}

if (enableMocks) {
  worker.use(
    // @ts-expect-error endpoint not implemented yet
    typedHttp.get('/v0.1/partners/global/shared-fields', () =>
      HttpResponse.json({
        items: buildFakeFields(),
      }),
    ),
  );
}
const isEnabled = defineModel<boolean>('isEnabled');

const sharedPartnerFields = ref<PartnerOption[]>([]);

const partnersStore = usePartnersStore();
const sourcesStore = useSourcesStore();
const feedsStore = useFeedsStore();

const { crmFeeds } = storeToRefs(feedsStore);

const integrationCRM = computed(() => {
  return crmFeeds.value.find(
    (feed) => feed.integration.type === integrationProfileType,
  );
});

const integrationCRMSources = computed(() => {
  const accountSource = sourcesStore
    .getSourcesByMdmType('account')
    ?.find(
      (source) =>
        source.feed_id === integrationCRM.value?.id &&
        source.mdm_type === 'account',
    );

  if (!accountSource) return [];

  return accountSource?.fields
    .filter((f) => f.data_type === 'string')
    .map((f) => ({
      label: f.nickname as string,
      value: f.id as number,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

function deleteMapping(index: number) {
  console.log('finding mapping to delete', index);
}

function updateMapping(mapping: Mapping) {
  fakeMappingOne.value = mapping;
}

const mapPartnersToFields = (field: SharedPartnerField) => {
  const { organization_uuid, nickname, source_field_id } = field;
  const partner = partnersStore.getPartnerOrgByUuid(organization_uuid);
  if (!partner) {
    return null;
  }
  return {
    ...partner,
    users: partner.users ?? [],
    tags: partner.tags ?? [],
    label: nickname,
    value: source_field_id,
  };
};

onBeforeMount(async () => {
  if (!enableMocks) return;

  // @ts-expect-error endpoint not implemented yet
  const response = (await crossbeamApi.GET<{
    items: SharedPartnerField[];
  }>('/v0.1/partners/global/shared-fields')) as {
    data: {
      items: SharedPartnerField[];
    };
  };
  const { data } = response;

  if (!data) return;

  sharedPartnerFields.value = data.items
    .reduce((acc, curr) => {
      const mappedField = mapPartnersToFields(curr);
      if (mappedField) {
        acc.push(mappedField);
      }
      return acc;
    }, [] as PartnerOption[])
    .sort((a, b) => a.name.localeCompare(b.name));
});
</script>
